import { useState, type FormEvent, type ReactNode } from "react";
import type { CmmdProjectMilestone, UpdateCmmdProjectMilestoneParams } from "@cmmd-forge/contracts";
import {
  ArrowLeftIcon,
  CalendarCheckIcon,
  CalendarRangeIcon,
  DiamondIcon,
  Link2Icon,
} from "lucide-react";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { Button } from "~/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ProjectMilestoneDeleteDialog } from "./ProjectMilestoneDeleteDialog";
import { ProjectTaskPicker, type ProjectTaskPickerOption } from "./ProjectTaskPicker";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";

const MILESTONE_DESCRIPTION_TEMPLATE = `## Outcome

Describe the observable result this milestone delivers.

## In scope

- Supported users and surfaces

## Exit criteria

- Measurable release gate

## Required proof

- Production evidence and exact links

## Non-goals

- Explicitly deferred work

## Risks and dependencies

- Known blocker or dependency`;

const STATUS_OPTIONS: readonly ProjectTaskPickerOption[] = [
  { value: "pending", label: "Pending", visual: { kind: "milestone", status: "pending" } },
  {
    value: "in_progress",
    label: "In progress",
    visual: { kind: "milestone", status: "in_progress" },
  },
  {
    value: "completed",
    label: "Completed",
    visual: { kind: "milestone", status: "completed" },
  },
  { value: "overdue", label: "Overdue", visual: { kind: "milestone", status: "overdue" } },
];

interface ProjectMilestoneDetailsViewProps {
  readonly milestone: CmmdProjectMilestone;
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly mutating: boolean;
  readonly error: string | null;
  readonly onBack: () => void;
  readonly onSave: (input: UpdateCmmdProjectMilestoneParams) => Promise<boolean>;
  readonly onDelete: () => Promise<boolean>;
}

export function ProjectMilestoneDetailsView(props: ProjectMilestoneDetailsViewProps) {
  const [title, setTitle] = useState(props.milestone.title);
  const [description, setDescription] = useState(props.milestone.description ?? "");
  const [status, setStatus] = useState<CmmdProjectMilestone["status"]>(props.milestone.status);
  const [dependsOnId, setDependsOnId] = useState<number | null>(props.milestone.dependsOnId);
  const [startDate, setStartDate] = useState(dateInputValue(props.milestone.startDate));
  const [targetDate, setTargetDate] = useState(dateInputValue(props.milestone.endDate));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dependencyOptions = props.milestones
    .filter((milestone) => milestone.id !== props.milestone.id)
    .map<ProjectTaskPickerOption>((milestone) => ({
      value: String(milestone.id),
      label: milestone.title,
      visual: { kind: "milestone", status: milestone.status },
    }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) return;
    await props.onSave({
      title: trimmedTitle,
      description: description.trim().length > 0 ? description : null,
      status,
      dependsOnId,
      startDate: startDate.length > 0 ? startDate : null,
      targetDate: targetDate.length > 0 ? targetDate : null,
    });
  };

  return (
    <>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => void submit(event)}>
        <DialogHeader className="gap-1 px-4 pb-3 pt-4 md:px-6 md:pt-6">
          <div className="flex min-w-0 items-center gap-2 pr-10">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={RESPONSIVE_ICON_BUTTON_CLASS}
              aria-label="Back to milestones"
              onClick={props.onBack}
            >
              <ArrowLeftIcon aria-hidden />
            </Button>
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">Edit milestone</DialogTitle>
              <DialogDescription className="truncate">
                Define the release checkpoint and the proof needed to leave it.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogPanel className="space-y-5 px-4 pb-5 md:px-6">
          <div className="space-y-1.5">
            <Label
              htmlFor={`milestone-${props.milestone.id}-title`}
              className="flex items-center gap-1.5"
            >
              <DiamondIcon className="size-4 text-muted-foreground" aria-hidden />
              Milestone title
            </Label>
            <Input
              id={`milestone-${props.milestone.id}-title`}
              aria-label="Milestone title"
              value={title}
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex min-h-touch items-center justify-between gap-3 md:min-h-8">
              <div>
                <p className="text-sm font-medium text-foreground">Release brief</p>
                <p className="text-sm text-muted-foreground md:text-xs">
                  Outcome, scope, exit criteria, required proof, and non-goals.
                </p>
              </div>
              {description.trim().length === 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={RESPONSIVE_TEXT_BUTTON_CLASS}
                  onClick={() => setDescription(MILESTONE_DESCRIPTION_TEMPLATE)}
                >
                  Use template
                </Button>
              ) : null}
            </div>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              ariaLabel="Milestone description"
              placeholder="Describe the milestone outcome and exit criteria…"
              density="compact"
              className="[&_[contenteditable]]:min-h-48 [&_textarea]:min-h-48"
            />
          </div>

          <div className="grid gap-4 border-t border-border pt-5 md:grid-cols-2">
            <MilestonePickerField icon={<DiamondIcon aria-hidden />} label="Status">
              <ProjectTaskPicker
                ariaLabel="Milestone status"
                value={status}
                emptyLabel="Status"
                options={STATUS_OPTIONS}
                disabled={props.mutating}
                onValueChange={(value) =>
                  setStatus((value ?? "pending") as CmmdProjectMilestone["status"])
                }
              />
            </MilestonePickerField>
            <MilestonePickerField icon={<Link2Icon aria-hidden />} label="Depends on">
              <ProjectTaskPicker
                ariaLabel="Milestone dependency"
                value={dependsOnId == null ? null : String(dependsOnId)}
                emptyLabel="No dependency"
                options={dependencyOptions}
                disabled={props.mutating}
                emptyVisual={{ kind: "milestone", status: null }}
                onValueChange={(value) => setDependsOnId(value == null ? null : Number(value))}
              />
            </MilestonePickerField>
            <MilestoneDateField
              id={`milestone-${props.milestone.id}-start-date`}
              label="Start date"
              ariaLabel="Milestone start date"
              value={startDate}
              icon={<CalendarRangeIcon aria-hidden />}
              onChange={setStartDate}
            />
            <MilestoneDateField
              id={`milestone-${props.milestone.id}-target-date`}
              label="Target date"
              ariaLabel="Milestone target date"
              value={targetDate}
              icon={<CalendarCheckIcon aria-hidden />}
              onChange={setTargetDate}
            />
          </div>

          {props.error != null ? (
            <p role="alert" className="text-sm text-destructive">
              {props.error}
            </p>
          ) : null}
        </DialogPanel>

        <DialogFooter
          className="flex-row flex-wrap items-center justify-between gap-2"
          variant="default"
        >
          <Button
            type="button"
            variant="ghost"
            className={`${RESPONSIVE_TEXT_BUTTON_CLASS} text-destructive`}
            disabled={props.mutating}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              onClick={props.onBack}
            >
              Back
            </Button>
            <Button
              type="submit"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={title.trim().length === 0 || props.mutating}
            >
              {props.mutating ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </DialogFooter>
      </form>
      <ProjectMilestoneDeleteDialog
        open={confirmDelete}
        mutating={props.mutating}
        onOpenChange={setConfirmDelete}
        onConfirm={async () => {
          const deleted = await props.onDelete();
          if (deleted) setConfirmDelete(false);
        }}
      />
    </>
  );
}

function MilestonePickerField(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <span className="text-muted-foreground [&_svg]:size-4">{props.icon}</span>
        {props.label}
      </p>
      {props.children}
    </div>
  );
}

function MilestoneDateField(props: {
  readonly id: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly value: string;
  readonly icon: ReactNode;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={props.id} className="flex items-center gap-1.5">
        <span className="text-muted-foreground [&_svg]:size-4">{props.icon}</span>
        {props.label}
      </Label>
      <Input
        id={props.id}
        aria-label={props.ariaLabel}
        type="date"
        value={props.value}
        onChange={(event) => props.onChange(event.currentTarget.value)}
      />
    </div>
  );
}

function dateInputValue(value: string | null): string {
  return value?.slice(0, 10) ?? "";
}
